import { supabase } from "@/integrations/supabase/client";

/** The existing Say It In Sports newsletter, also linked in the footer. */
export const NEWSLETTER_URL = "https://sayitinsports.beehiiv.com/subscribe";

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

/** Beehiiv's hosted subscribe page, pre-filled with the address the visitor typed. */
export const buildSubscribeUrl = (email: string, utmContent = "wave-campaign"): string => {
  const url = new URL(NEWSLETTER_URL);
  url.searchParams.set("email", email.trim());
  url.searchParams.set("utm_source", "sayitinsports");
  url.searchParams.set("utm_medium", "site");
  url.searchParams.set("utm_campaign", "know-her-name");
  url.searchParams.set("utm_content", utmContent);
  return url.toString();
};

/** Where the reader opted in. Mirrors the `siis_signup_source` custom field. */
export type NewsletterSignupSource = "wave_campaign" | "athlete_profile" | "main_site";

export type NewsletterContext = {
  signupSource: NewsletterSignupSource;
  /** `jersey_shore_wave` when the opt-in belongs to that campaign. */
  campaign?: string | null;
  /** Athlete slug or name when the box was ticked on an athlete profile. */
  athleteInterest?: string | null;
  /** Path the reader was on when opting in. */
  entryPage?: string | null;
};

/**
 * One shared SIIS Weekly audience — never a list per athlete. Only ever called
 * when the reader explicitly ticks the optional newsletter box. Never throws
 * and only reports "accepted" when the provider confirms the request.
 */
export const submitNewsletterOptIn = async (
  email: string,
  context: NewsletterContext,
): Promise<"accepted" | "unconfigured" | "failed"> => {
  try {
    const { data, error } = await supabase.functions.invoke("newsletter-subscribe", {
      body: {
        email: email.trim(),
        signup_source: context.signupSource,
        campaign: context.campaign ?? null,
        athlete_interest: context.athleteInterest ?? null,
        entry_page: context.entryPage ?? null,
      },
    });
    if (error) return "failed";
    const status = (data as { status?: string } | null)?.status;
    return status === "accepted" || status === "unconfigured" ? status : "failed";
  } catch {
    return "failed";
  }
};
